import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  FileSpreadsheet, ArrowLeft, Loader2, Eye, Trash2, Phone, Mail, Building, Calendar, MapPin, DollarSign,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPE_LABELS = {
  rental: 'Location',
  purchase: 'Achat',
  parts: 'Pièces',
  software: 'Logiciel',
  service: 'Service',
  other: 'Autre',
};

const BUDGET_LABELS = {
  '<5k': 'Moins de 5 000 $',
  '5-15k': '5 000 $ - 15 000 $',
  '15-50k': '15 000 $ - 50 000 $',
  '50k+': 'Plus de 50 000 $',
  discuss: 'À discuter',
};

const STATUS_CONFIG = {
  new: { label: 'Nouveau', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  in_review: { label: 'En analyse', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  quoted: { label: 'Soumis', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  won: { label: 'Gagné', color: 'bg-green-100 text-green-800 border-green-200' },
  lost: { label: 'Perdu', color: 'bg-gray-100 text-gray-700 border-gray-200' },
};

export default function AdminQuoteRequests() {
  const { token, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isAdmin()) {
      navigate('/');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${token}` };
        const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
        const [listRes, statsRes] = await Promise.all([
          axios.get(`${API}/admin/quote-requests${params}`, { headers }),
          axios.get(`${API}/admin/quote-requests/stats`, { headers }),
        ]);
        if (cancelled) return;
        setItems(listRes.data);
        setStats(statsRes.data);
      } catch (error) {
        if (!cancelled) toast.error('Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, isAuthenticated, isAdmin, navigate, token, statusFilter, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const updateField = async (id, payload) => {
    try {
      setUpdating(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/admin/quote-requests/${id}`, payload, { headers });
      toast.success('Mise à jour effectuée');
      setSelected((prev) => (prev && prev.id === id ? { ...prev, ...payload } : prev));
      refresh();
    } catch (error) {
      toast.error('Erreur de mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Supprimer cette demande?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/quote-requests/${id}`, { headers });
      toast.success('Demande supprimée');
      setSelected(null);
      refresh();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openDetail = async (item) => {
    setSelected(item);
    setAdminNotes(item.admin_notes || '');
    if (!item.read) {
      await updateField(item.id, { read: true });
    }
  };

  const formatDate = (s) =>
    new Date(s).toLocaleDateString('fr-CA', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 text-black animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-quote-requests-page">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin">
            <Button variant="ghost" size="sm" className="text-gray-700 hover:text-black">
              <ArrowLeft className="h-4 w-4 mr-2" /> Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-black flex items-center gap-2">
              <FileSpreadsheet className="h-7 w-7" />
              Demandes de soumission
            </h1>
            <p className="text-gray-500 text-sm">Suivi des prospects et des soumissions</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-2xl font-bold text-black" data-testid="quote-stat-total">{stats.total}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500">Non lues</p>
                <p className="text-2xl font-bold text-black" data-testid="quote-stat-unread">{stats.unread}</p>
              </CardContent>
            </Card>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <Card key={key} className="bg-white border-gray-200">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500">{cfg.label}</p>
                  <p className="text-2xl font-bold text-black">{stats.by_status?.[key] || 0}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-white border-gray-300" data-testid="quote-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([k, c]) => (
                <SelectItem key={k} value={k}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-gray-500">{items.length} demande{items.length !== 1 ? 's' : ''}</p>
        </div>

        {/* List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : items.length === 0 ? (
          <Card className="bg-white border-gray-200">
            <CardContent className="p-12 text-center text-gray-500">
              Aucune demande pour ce filtre.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((item) => {
              const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.new;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card
                    className={`bg-white border ${!item.read ? 'border-black' : 'border-gray-200'} hover:shadow-md transition-shadow cursor-pointer`}
                    onClick={() => openDetail(item)}
                    data-testid={`quote-row-${item.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {!item.read && <span className="w-2 h-2 bg-black rounded-full" />}
                            <p className="font-semibold text-black truncate">{item.name}</p>
                            {item.company && (
                              <span className="text-sm text-gray-500">— {item.company}</span>
                            )}
                            <Badge className={`${statusCfg.color} border text-xs`}>
                              {statusCfg.label}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {PROJECT_TYPE_LABELS[item.project_type] || item.project_type}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-700 truncate">{item.equipment_description}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-2 flex-wrap">
                            <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{item.email}</span>
                            <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{item.phone}</span>
                            {item.budget_range && (
                              <span className="flex items-center gap-1"><DollarSign className="h-3 w-3" />{BUDGET_LABELS[item.budget_range]}</span>
                            )}
                            <span>{formatDate(item.created_at)}</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); openDetail(item); }}
                          className="border-gray-300"
                          data-testid={`quote-view-${item.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" /> Voir
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-black flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  Demande de {selected.name}
                </DialogTitle>
                <DialogDescription className="text-gray-500">
                  Reçue le {formatDate(selected.created_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                {/* Status & Type */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Statut</label>
                    <Select
                      value={selected.status}
                      onValueChange={(v) => updateField(selected.id, { status: v })}
                      disabled={updating}
                    >
                      <SelectTrigger className="bg-white border-gray-300" data-testid="quote-detail-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {Object.entries(STATUS_CONFIG).map(([k, c]) => (
                          <SelectItem key={k} value={k}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Type de projet</label>
                    <div className="px-3 py-2 bg-gray-50 rounded border border-gray-200 text-sm">
                      {PROJECT_TYPE_LABELS[selected.project_type] || selected.project_type}
                    </div>
                  </div>
                </div>

                {/* Contact info */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-gray-500" />
                    <a href={`mailto:${selected.email}`} className="text-black hover:underline">{selected.email}</a>
                  </p>
                  <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-gray-500" />
                    <a href={`tel:${selected.phone}`} className="text-black hover:underline">{selected.phone}</a>
                  </p>
                  {selected.company && (
                    <p className="flex items-center gap-2"><Building className="h-4 w-4 text-gray-500" />{selected.company}</p>
                  )}
                  {selected.location && (
                    <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gray-500" />{selected.location}</p>
                  )}
                </div>

                {/* Equipment */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Équipement souhaité</label>
                  <p className="text-sm text-black bg-gray-50 rounded p-3 border border-gray-200">
                    {selected.equipment_description}
                  </p>
                </div>

                {/* Rental dates */}
                {(selected.rental_start_date || selected.rental_end_date) && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 text-sm">
                    <p className="flex items-center gap-2 text-blue-900">
                      <Calendar className="h-4 w-4" />
                      <strong>Dates de location :</strong>
                      {selected.rental_start_date || '—'} → {selected.rental_end_date || '—'}
                    </p>
                  </div>
                )}

                {/* Budget */}
                {selected.budget_range && (
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Budget</label>
                    <p className="text-sm text-black">{BUDGET_LABELS[selected.budget_range]}</p>
                  </div>
                )}

                {/* Details */}
                {selected.details && (
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Précisions</label>
                    <p className="text-sm text-black bg-gray-50 rounded p-3 border border-gray-200 whitespace-pre-wrap">
                      {selected.details}
                    </p>
                  </div>
                )}

                {/* Admin notes */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Notes internes</label>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    onBlur={() => {
                      if (adminNotes !== (selected.admin_notes || '')) {
                        updateField(selected.id, { admin_notes: adminNotes });
                      }
                    }}
                    rows={3}
                    placeholder="Notes pour l'équipe (sauvegardé automatiquement)..."
                    className="bg-white border-gray-300"
                    data-testid="quote-admin-notes"
                  />
                </div>
              </div>

              <DialogFooter className="flex justify-between items-center">
                <Button
                  variant="ghost"
                  onClick={() => deleteItem(selected.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="quote-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                </Button>
                <Button onClick={() => setSelected(null)} className="bg-black text-white hover:bg-gray-800">
                  Fermer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
